/**
 * Minimal toast store + hook (shadcn-style) built on @radix-ui/react-toast.
 * Any module can call `toast({ title, description, variant })`; the
 * `<Toaster>` mounted in the root layout listens and renders.
 *
 * Keep it small — no dedupe, no timers beyond Radix's built-in auto-close.
 */
import { useEffect, useState } from "react";
import type { ToastProps, ToastVariant } from "./toast";

type ToastInput = Omit<ToastProps, "open" | "onOpenChange" | "variant"> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
};

export type ToasterToast = ToastInput & { id: string };

const listeners = new Set<(toasts: ToasterToast[]) => void>();
let memory: ToasterToast[] = [];

function emit() {
  for (const l of listeners) l(memory);
}

export function toast(input: ToastInput) {
  const id = String(Date.now() + Math.random());
  memory = [...memory, { ...input, id }];
  emit();
  return id;
}

export function dismissToast(id: string) {
  memory = memory.filter((t) => t.id !== id);
  emit();
}

export function useToast() {
  const [toasts, setToasts] = useState<ToasterToast[]>(memory);
  useEffect(() => {
    listeners.add(setToasts);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);
  return { toasts, toast, dismissToast };
}
